import { SkipEvent } from '../domain/SkipEvent';
import { SkipsProcessingJob } from '../domain/SkipsProcessingJob';
import logger from '../../logger';

/**
 * Service for handling business logic related to skips processing
 */
export class SkipsProcessingService {
  /**
   * Saves all changes in a skip event to Airtable
   * - Updates all changed fields
   * - Adds appropriate job flags
   */
  async saveSkipEvent(skipEvent: SkipEvent): Promise<void> {
    const promises: Promise<void>[] = [];

    if (skipEvent.archivedJob) {
      promises.push(this.saveJob(skipEvent.archivedJob, 'Skips - Final Approval'));
    }

    if (skipEvent.continuationJob) {
      promises.push(this.saveJob(skipEvent.continuationJob, 'Skips - Reviewed'));
    }

    await Promise.all(promises);

    await logger.log('SkipsProcessingService - saveSkipEvent completed', {
      skipEventId: skipEvent.getId(),
      archivedJobId: skipEvent.archivedJob?.id,
      continuationJobId: skipEvent.continuationJob?.id,
    });
  }

  /**
   * Saves a single job's changes and adds the specified flag
   */
  private async saveJob(job: SkipsProcessingJob, flagToAdd: string): Promise<void> {
    const changes = job.getChangedFields();

    // Apply all changes
    for (const [field, value] of Object.entries(changes)) {
      await job.jobRecord.set(field as any, value);
    }

    // Add job flag
    const currentFlags = job.jobFlags;
    const newFlags = this.addJobFlag(currentFlags, flagToAdd);
    await job.jobRecord.set('Job Flags', newFlags);

    // Sync to Airtable
    await job.jobRecord.sync();

    await logger.log('SkipsProcessingService - saveJob', {
      jobId: job.id,
      jobName: job.name,
      changes: Object.keys(changes),
      flagAdded: flagToAdd,
    });
  }

  /**
   * Adds a flag to the job flags array, avoiding duplicates
   */
  addJobFlag(existingFlags: string[], newFlag: string): string[] {
    const trimmedNewFlag = newFlag.trim();

    if (!existingFlags.includes(trimmedNewFlag)) {
      return [...existingFlags, trimmedNewFlag];
    }

    return existingFlags;
  }

  /**
   * Groups jobs into skip events by pairing archived and continuation jobs
   */
  groupJobsIntoSkipEvents(jobs: SkipsProcessingJob[]): SkipEvent[] {
    const skipEvents: SkipEvent[] = [];
    const processedJobIds = new Set<string>();

    for (const job of jobs) {
      if (processedJobIds.has(job.id)) {
        continue;
      }

      if (job.isArchivedJob) {
        // This is an archived job - find its continuation
        const contJobId = job.contJobLink[0];
        const continuationJob = contJobId ? jobs.find((j) => j.id === contJobId) || null : null;

        processedJobIds.add(job.id);

        if (continuationJob) {
          processedJobIds.add(continuationJob.id);
        }

        skipEvents.push(new SkipEvent(job, continuationJob));
      } else if (job.isContinuationJob) {
        // This is a continuation job - check if archived job was already processed
        const archJobId = job.archiveJobLink[0];
        const archivedJob = archJobId ? jobs.find((j) => j.id === archJobId) || null : null;

        if (archivedJob && processedJobIds.has(archivedJob.id)) {
          // Already created skip event when we processed the archived job
          continue;
        }

        // Continuation job without archived or archived not yet processed
        processedJobIds.add(job.id);

        if (archivedJob) {
          processedJobIds.add(archivedJob.id);
          skipEvents.push(new SkipEvent(archivedJob, job));
        } else {
          skipEvents.push(new SkipEvent(null, job));
        }
      } else {
        // Single job (neither archived nor continuation)
        processedJobIds.add(job.id);
        skipEvents.push(new SkipEvent(job, null));
      }
    }

    return skipEvents;
  }
}
